import { IDashboardMetadata, IDashboardSectionMetadata } from '../dashboard-metadata.service';
import { Chart } from '../../chart/base-chart.component';
import { IObject } from '@type_definitions/datatypes';
import { CustomerDashboardFiltersComponent } from '../filters/customer/customer-dashboard-filters.component';
import { DateType } from '@app/shared/services/date.util';
import { countryHitRateIndicator, REPORT_4_MAPPINGS, REPORTS_228_220_COLUMNS } from './shared-dashboard.config';
import { VALIDATOR_FN_CUSTOMER } from '../dashboard.service';
import { CustomerDashboardSubtitleComponent } from '../subtitle/customer-dashboard-subtitle.component';
import _ from 'underscore';

export const CUSTOMER_DASHBOARD_CONFIG:IDashboardMetadata = {
  title: 'Customer KPI',
  filterComponent: {
    name: CustomerDashboardFiltersComponent,
    inputs: {
      preselectGbdm: true,
      showLamContact: true,
      showSubCompany: true,
      showTestOrder: true,
      validatorFn: VALIDATOR_FN_CUSTOMER
    }
  },
  subtitleComponent: {
    name: CustomerDashboardSubtitleComponent,
    inputs: {}
  },
  chartLink: Chart.CUSTOMER,
  mapping: {
    // we cannot put currency here, because report 8 cannot cope with that
    // Decurce: 'currency.id',
    Devincl: 'vatIncl.id',
    Desprnm: 'customer.id',
    Cucomid: 'customerCompany.id',
    Lacgrid: 'lamGroup.id',
    Lacomnm: 'lamCompany.id',
    Delamcn: 'lamContact.id',
    Delamnm: 'lam.id',
    Decounm: 'country.id',
    Gblname: 'gbdm.id',
    Decsunm: 'subCompany.id',
    Desk: 'gbdmDesk.id',
    Detestr: 'testOrder.id'
  },
  fixedFilters: {
    Devincl: '0'
  },
  groups: [
    {
      key: 'ACTIVATION',
      reports: [
        {
          id: 175,
          key: 'COUNTRY_HIT_RATE',
          storedReport: 'COUNTRY_STATUS_PLANNING_NEXT_YEAR',
          fixedFilters: {
            Deordpr_ds: '',
            Displaycontacts: 'GAM'
          },
          mapping: {
            Decusid: 'customerCompany.id',
            Lacgrid: 'lamGroup.id',
            Delamco: 'lamCompany.id',
            Delamnm: 'lam.id'
          },
          columns: [
            'Decounm',
            'Webshopsetup',
            'Desprnm',
            {
              name: 'Countryhitrate',
              groupingSequence: 0
            },
            {
              name: 'Has_locations_ds',
              groupingSequence: 1
            },
            'Pacomnm',
            'Deexpoy',
            'Detotal',
            'Detotlo',
            'Losprst_ds',
            'Lostaco',
            'Losprdt',
            'Denrusr',
            'Deordpr_ds',
            'Icprodt',
            'Deucnok',
            'Delamcn',
            'Cglname',
            'Gblname',
            'Improdt',
            'Derqbco',
            'Desprst_ds',
            'Destaco',
            'Deordga_ds'
          ],
          validations: [
            {
              expression: (model: IObject): boolean => !!model?.customer?.id,
              message: 'This section is only loaded when a Customer is selected in the search filters'
            }
          ],
          indicatorFn: countryHitRateIndicator
        },
        {
          browselistCode: 'WEBSHOP_INFO',
          key: 'WAITING_FOR_CATALOG',
          comments: ' ',
          fixedFilters: {
            Decatav_ds: '1',
            Decatal: ' is null '
          },
          mapping: REPORT_4_MAPPINGS,
          columns: [
            'Desprnm',
            'Desprst_ds',
            'Desprds',
            'Delamcm',
            'Delamcn',
            'Decatdt',
            'Destadt',
            'Destaco'
          ]
        },
        {
          browselistCode: 'WEBSHOP_INFO',
          key: 'OUTDATED_CATALOG',
          comments: ' ',
          fixedFilters: {
            Decatal: ' is not null ',
            Mxprodt_TO: 'sysdate-180'
          },
          mapping: REPORT_4_MAPPINGS,
          columns: [
            'Desprnm',
            'Desprst_ds',
            'Desprds',
            'Delamcm',
            'Delamcn',
            'Mxprodt',
            'Destadt',
            'Destaco'
          ]
        }
      ]
    },
    {
      key: 'INVOICE',
      reports: [
        {
          id: 85,
          key: 'EXPIRED_LOCAL_INVOICES',
          fixedFilters: {
            Deexpdt: '>=5',
            Deexpir_ds: 1,
            Deshort: null
          },
          mapping: {
            Decomnm: 'customerCompany.id',
            Degbdmn: 'gbdm.id'
          },
          columns: [
            'Avg_payment_terms',
            'Dereqid',
            'Depornr', // PO #
            'Desprnm',
            'Decomds',
            'Deinvnr',
            'Deinvdt',
            'Detotal', // Invoice total
            'Decurce', // Currency
            'Deconnm', // Invoice contact
            'Deemail', // Invoice email
            'Depaydt', // Payment date
            'Depayst_ds',
            'Decommt_sh',
            'Oscommt', // Order comment
            'Days_pending',
            {
              name: 'Decomnm',
              label: 'Company'
            },
            'Decounm',
            'Pacomnm',
            'Decodnm',
            'Degbdmn',
            'Lam',
            'Decsunm',
            'Stocktype'
          ]
        },

        {
          id: 8,
          key: 'EXPIRED_CENTRAL_INVOICES',
          fixedFilters: {
            Deexpdt: '>= -5',
            Denonre_ds: 0,
            Deshort: null,
            Depayst_ds: '0,1,7,8',
            Deinvty_ds: 1,
            Deinvcr_ds: 'F,C',
            Delednr: '700120,700210,703010'
          },
          mapping: {
            Desprid: 'customer.id',
            Decomnm: 'customerCompany.name',
            Decouid: 'country.id'
          },
          columns: [
            'Avg_payment_terms',
            'Avg_approval_time',
            {
              name: 'Dereqid',
              label: 'ACSL #'
            },
            'Depornr', // PO #
            'Deinvnr',
            'Deinvdt',
            'Depayst_ds',
            {
              name: 'Decomnm',
              label: 'Company'
            },
            'Decounm',
            'Gblname',
            'Desprnm',
            'Decommt',
            'Depaydt',
            'Decsunm'
          ]
        }
      ]
    },
    {
      key: 'NEW',
      reports: [
        {
          id: 220,
          key: 'ORDERS_NOT_PLACED',
          comments: `Orders that have not been placed by the reseller <br>\n ` +
            `      This means that the order was submitted and:\n ` +
            `      <ul>\n` +
            `        <li>Has no placement date</li>\n` +
            `        <li>Or is in state \'Validated\'</li>\n` +
            `        <li>Or doesn\'t have one of the \'BP to Distri\', \'BP to OEM\' or \'Distri to OEM\' numbers (depending on order placement type)</li>\n` +
            `      </ul>`,
          fixedFilters: {
            Kpitype: 'ORDERS_NOT_PLACED'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Dereqid',
            'Depornr', // PO #
            'Decounm',
            'Desubdt',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Detotal',
            'Dereqst',
            'Decomds',
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Stocktype'
          ]
        },

        {
          id: 220,
          key: 'ORDERS_SAVED',
          comments: ' ',
          fixedFilters: {
            Kpitype: 'ORDERS_SAVED'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: REPORTS_228_220_COLUMNS
        },

        {
          id: 220,
          key: 'ORDERS_NO_OEM',
          comments: ' ',
          fixedFilters: {
            Kpitype: 'ORDERS_NO_OEM',
            Need_oem_nr: 1
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Dereqid',
            'Depornr', // PO #
            'Desubdt',
            'Depladt',
            'Deetadt',
            'Decounm',
            'Debpref',
            'Debdref',
            'Dedvref',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Dereqst',
            'Decomds',
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Stocktype'
          ]
        },
        {
          id: 222,
          key: 'QUOTES_WITHOUT_OFFER',
          comments: 'Quotes that haven\'t had an offer for over 2 business days',
          fixedFilters: {
            Dereqst: 'SUBMIT',
            Dequdat: 2
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Urgent_order_description',
            'Dereqid',
            'Depornr', // PO #
            'Decounm',
            'Desubdt',
            'Dersudt',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Detotal',
            'Dereqst',// Status
            'Decomds',// Detailed status
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Stocktype'
          ]
        },

        {
          id: 222,
          key: 'QUOTES_FOR_APPROVAL',
          comments: 'Quotes waiting for approval',
          fixedFilters: {
            Dereqst: 'CUSTOMER'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Urgent_order_description',
            'Dereqid',
            'Depornr', // PO #
            'Decounm',
            'Desubdt',
            'Dersudt',
            'Devaldt',
            'Lead_time',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Detotal',
            'Dereqst',// Status
            'Decomds',// Detailed status
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Dereqnm', // Order requester
            'Stocktype'
          ]
        },
        {
          id: 222,
          key: 'QUOTES_NEW_PRICES',
          comments: 'Quotes with new catalog prices',
          fixedFilters: {
            Deqstat: 'NEED_RECAL'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Urgent_order_description',
            'Dereqid',
            'Depornr', // PO #
            'Decounm',
            'Desubdt',
            'Dersudt',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Detotal',
            'Dereqst',// Status
            'Decomds',// Detailed status
            'Lead_time',
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Stocktype'
          ]
        },
        {
          id: 222,
          key: 'QUOTES_SAVED',
          comments: 'Quotes saved for later',
          fixedFilters: {
            Dereqst: 'CREATED'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Urgent_order_description',
            'Dereqid',
            'Depornr', // PO #
            'Decounm',
            'Decredt',
            'Decommt',
            'Pacomnm',
            'Odlname',
            'Detotal',
            'Actotal',
            'Detotal',
            'Dereqst',// Status
            'Decomds',// Detailed status
            'Lead_time',
            'Decompa',
            'Delamnm',
            'Lacomnm',
            'Gblname',
            'Decsunm',
            'Dereqnm', // Order requester
            'Requester_email_address',
            'Stocktype'
          ]
        }
      ]
    },
    {
      key: 'PENDING',
      reports: [
        getPendingKPIConfig('PENDING_CUSTOMER', 'C'),
        getPendingKPIConfig('PENDING_BP', 'P'),
        getPendingKPIConfig('PENDING_VENDOR', 'V'),
        getPendingKPIConfig('PENDING_DISTRIBUTOR', 'D'),
        getPendingKPIConfig('PENDING_ACSL', 'A'),
        getPendingKPIConfig('PENDING_CATALOG', 'CATOWNER')
      ]
    },
    {
      key: 'B2B',
      reports: [
        {
          id: 66,
          key: 'FAILED_B2B_ORDERS',
          comments: 'Failed B2B order requests since last week. Please note that some of these don\'t even have a webshop selected yet. Because the requests failed, they cannot be related to a specific OD or BP',
          fixedFilters: {
            Deoutgo_ds: 0,
            Demesdt_FROM: DateType.ONE_WEEK_AGO,
            Demesty: 'OrderRequest',
            Status_ok: 0
          },
          mapping: {
            Detestr_ds: 'testOrder.id'
          }
        },
        {
          id: 316,
          key: 'FAILED_B2B_INVOICES',
          storedReport: 'FAILED_B2B_INVOICES',
          mapping: {
            Countryname: 'country.id',
            Webshopname: 'customer.id',
            Customerid: 'customerCompany.id',
            Gbdm: 'gbdm.id',
            Gbdm_desk: 'gbdmDesk.id',
            Lam_group: 'lamGroup.id',
            Lam_company: 'lamCompany.id',
            Lam_contact: 'lamContact.id',
            Lam: 'lam.id'
          }
        },
        {
          id: 223,
          key: 'PENDING_B2B_NON_CATALOG',
          comments: '',
          fixedFilters: {
            Dereqst: '255,229' // pending b2b - non catalog request
          },
          mapping: {
            Desk: 'gbdmDesk.id'
          },
          columns: [
            'Devipnr_ds',
            'Dereqid_ds',
            'Depornr',
            'Pending_date',
            'Age',
            'Dereqst',
            'Owner_type',
            'Owner',
            'Decommt',
            'Decompa',
            'Decounm',
            'Detotal',
            'Actotal',
            'Desubdt',
            'Odlname',
            'Pacomnm',
            'Delamnm',
            'Lacomnm',
            'Delamcn',
            'Decsunm',
            'Decomds',
            'Customer_kpi_section',
            'Kpi_dashboard_name',
            'Gblname',
            'Desk',
            'Deetadt',
            'Doityourself_ds',
            'Oddesk',
            'Vnam',
            'Vrep',
            'Debpref',
            'Dedvref',
            'Deveref',
            'Stocktype',
            'Denamnm'
          ]
        },
        {
          id: 129,
          key: 'B2B_TICKETS',
          fixedFilters: {
            Shmtyds: 'TODO_PR_B2B Issues,TODO_PR_B2B,TODO_CO_B2B Issues,TODO_CO_B2B,TODO_IT_B2B',
            Deiopen: 1
          },
          mapping: {
            Shcounm: 'country.id',
            Shsprnm_ds: 'customer.id',
            Shlacom: 'lam.id',
            Shlamcn: 'lamContact.id',
            Shlamcm: 'lamCompany.id',
            Lacgrid: 'lamGroup.id',
            Shgbdmn: 'gbdm.id'
          }
        },
        {
          id: 279,
          key: 'PUNCHOUT_HIT_RATE',
          storedReport: 'PUNCHOUT_HIT_RATE',
          fixedFilters: {
            Calculate_counts: '1',
            Countpunchout: '>0',
            Requestdaterange_FROM: DateType.THIRTY_DAYS_AGO
          },
          mapping: {
            Desprid: 'customer.id',
            Decusid: 'customerCompany.id',
            Delamco_ds: 'lam.id'
          }
        },
        {
          id: 313,
          key: 'EXTERNAL_RELATIONS',
          mapping: {
            Customer_name: 'customerCompany.id',
            Webshop_id: 'customer.id',
            Gbdm_name: 'gbdm.id',
            Gbdm_desk: 'gbdmDesk.id'
          }
        }
      ]
    },
    {
      key: 'ETA',
      reports: [
        {
          id: 77,
          key: 'ORDERS_ETA_LESS1W',
          comments: 'ETA <= 1 week (5 business days)',
          fixedFilters: {
            Deetady: 'between 0 and 5'
          },
          columns: [
            //(!),
            'Deordnr',//ACSL #
            'Depornr', // PO #
            {
              name: 'Debpref',
              label: 'BP to OEM #'
            },
            {
              name: 'Debdref',
              label: 'BP to Distri #'
            },
            'Dereqst',// Status
            'Decomds',// Detailed status
            'Decommt',//Comment
            {
              name: 'Deetadt',
              label: 'ETA date'
            },
            'Deshpdt',//Ship date
            {
              name: 'Desprnm',
              label: 'Company'
            },
            'Decounm',//Country
            'Pacomnm',//BP
            'Odlname',//OD
            'Delamnm',//LAM
            'Desubdt',//Submit date
            'Lacomnm',//LAM Company
            'Gblname',//GBDM
            'Denamnm',//NAM
            'Decsunm',//Subcompany
            'Stocktype'
          ]
        },
        {
          id: 226,
          key: 'ETA_EXPIRED',
          fixedFilters: {
            Dewdfid: '69'
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Devipnr_ds',
            'Dereqid',
            'Depornr',
            'Debpref',
            'Debdref',
            'Deveref',
            'Dereqst',
            'Decommt',
            'Deetadt',
            'Deshpdt',
            'Detotal',
            'Actotal',
            'Denamnm',
            'Decompa',
            'Decounm',
            'Pacomnm',
            'Odlname',
            'Oddesk',
            'Desubdt',
            'Delamnm',
            'Lacomnm',
            'Delamcn',
            'Gblname',
            'Decsunm',
            'Decomds',
            'Deenddt',
            'Vendor',
            'Vglname',
            'Stocktype',
            'Desk',
            'Doityourself_ds'
          ]
        }

      ]
    },
    {
      key: 'SHIPPED',
      reports: [
        {
          id: 227,
          key: 'SHIPPED_NOT_CLOSED',
          mapping: {
            Decurce: 'currency.id'
          }
        },
        {
          id: 224,
          key: 'NEED_SHIP_DATE',
          fixedFilters: {
            Kpitype: 'PLACED_INDIRECTLY'
          },
          mapping: {
            Decurce: 'currency.id'
          }
        },
        {
          id: 225,
          key: 'MISSING_INVOICE',
          mapping: {
            Decurce: 'currency.id'
          },
          fixedFilters: {
            Problem: '1,2'
          }
        },
        {
          id: 225,
          key: 'MISSING_ORDER_INFO',
          mapping: {
            Decurce: 'currency.id'
          },
          fixedFilters: {
            Problem: '3,4'
          }
        },
        {
          id: 205,
          key: 'MISSING_POD',
          fixedFilters: {
            Dedocok_pod: 0, // POD Uploaded -> no
            Deneeds_pod: 1, // POD requested -> Yes
            Dereqst: '3234846615', // Open,
            Deshipp: 1 // Shipped by customer -> Yes
          },
          columns: [
            'Devipnr_ds', // VIP
            'Dereqid', // ACSL #
            'Depornr', // PO #
            'Pod', // Upload POD
            'Dereqst', // Main status
            'Decommt', // Comment
            'Deshpdt', // Ship date
            'Desprnm', // Webshop
            'Decounm', // Country
            'Pacomnm', // BP
            'Odlname', // OD
            'Desubdt', // Submit date
            'Delamnm', // LAM
            'Lacomnm', // LAM company
            'Delamcn', // LAM contact
            'Gblname', // GBDM
            'Denamnm', // NAM
            'Decsunm', // Sub company
            'Decomds', // Detailed status
            'Desk', // GBDM Desk
            'Doityourself_ds', // DIY
            'Oddesk', // OD Desk
            'Stocktype' // Stock type
          ]
        }
      ]
    },
    {
      key: 'ALL_PO_PQ',
      reports: [
        {
          id: 32,
          key: 'LOST_ORDERS_QUOTES',
          fixedFilters: {
            Deactio: 37,
            Decandt_FROM: DateType.ONE_WEEK_AGO
          },
          mapping: {
            Degbdmn: 'gbdm.id',
            Lalamnm: 'lam.id',
            Lalamcm: 'lamCompany.id',
            Lagrpnm: 'lamGroup.id',
            Lalname: 'lamContact.id',
            Detestr_ds: 'testOrder.id'
          }
        },
        {
          id: 228,
          key: 'ALL_OPEN',
          mapping: {
            Decurce: 'currency.id'
          }
        },
        {
          id: 228,
          key: 'ALL_UNSUBMITTED_ORDERS_QUOTES',
          mapping: {
            Decurce: 'currency.id'
          },
          fixedFilters: {
            Dereqst: 'CREATED'
          },
          columns: _.map(REPORTS_228_220_COLUMNS, (columnName:string) => columnName === 'Dereqid' ? 'Dereqid_ds' : columnName)
        }
      ]
    },
    {
      key: 'TICKETS',
      reports: [
        {
          browselistCode: 'TICKET',
          key: 'OPEN_SERVICE_TICKETS',
          fixedFilters: {
            Deiopen: 1,
            Categorygroup: 'SERVICE'
          },
          mapping: {
            Shsprnm: 'customer.id',
            Lacgrid: 'lamGroup.id',
            Shlamcm: 'lamCompany.id',
            Shlacom: 'lam.id',
            Shgbdmn: 'gbdm.id',
            Shcounm: 'country.id',
            Shlamcn: 'lamContact.id'
          }
        },
        {
          browselistCode: 'TICKET',
          key: 'OPEN_TICKETS',
          fixedFilters: {
            Deiopen: 1
          },
          mapping: {
            Shsprnm: 'customer.id',
            Lacgrid: 'lamGroup.id',
            Shlamcm: 'lamCompany.id',
            Shlacom: 'lam.id',
            Shgbdmn: 'gbdm.id',
            Shcounm: 'country.id',
            Shlamcn: 'lamContact.id'
          }
        },
        {
          browselistCode: 'TICKET',
          key: 'CLOSED_TICKETS',
          fixedFilters: {
            Deiopen: 0,
            Dereadt_FROM: DateType.ONE_WEEK_AGO
          },
          mapping: {
            Shsprnm: 'customer.id',
            Lacgrid: 'lamGroup.id',
            Shlamcm: 'lamCompany.id',
            Shlacom: 'lam.id',
            Shgbdmn: 'gbdm.id',
            Shcounm: 'country.id',
            Shlamcn: 'lamContact.id'
          }
        }
      ]
    },
    {
      key: 'REPORTS',
      reports: [
        {
          id: 101,
          key: 'AUTOMATIC_REPORTS_BY_CUSTOMER',
          storedReport: 'AUTOMATIC_REPORTS_BY_CUSTOMER',
          mapping: {
            Desprnm: 'customer.id',
            Decomnm: 'customerCompany.id',
            Degbdmn: 'gbdm.id',
            Desk: 'gbdmDesk.id'
          }
        },
        {
          id: 133,
          key: 'MY_REPORTS',
          fixedFilters: {
            Demyfil: 0
          },
          mapping: {
            Decomnm: 'customerCompany.name',
            Desprid: 'customer.id'
          }
        },
        {
          id: 179,
          key: 'SURVEY_QUESTIONS',
          fixedFilters: {
            Answered: 0
          },
          mapping: {
            Decurce: 'currency.id'
          },
          columns: [
            'Dequest', //Question
            'Deanswe', //Answer
            'Decommt', //Comment
            'Decredt', //Updated on
            'Deconnm', //Updated by
            'Decomnm', //Company
            'Decounm', //Country
            'Dewflty', //Type
            'Dekeywo', //Keyword
            'Odlname', //OD
            'Gvlname' //GBDM OEM
          ],
          validations: [
            {
              expression: (model: IObject): boolean => model?.customer?.id || model?.customerCompany?.id,
              message: 'This section is only loaded when a Customer or Customer company is selected in the search filters'
            }
          ]
        }
      ]
    }
  ]
};

function getPendingKPIConfig(sectionTitleKey:string, type:string):IDashboardSectionMetadata {
  return {
    id: 223,
    key: sectionTitleKey,
    fixedFilters: {
      Customer_kpi_section: type,
      Exclude_steps : type === 'A'? '260,255,229':''
    },
    mapping: {
      Decurce: 'currency.id'
    },
    columns: [
      'Devipnr_ds',
      'Dereqid_ds',
      'Depornr',
      'Pending_date',
      'Age',
      'Dereqst',
      'Owner_type',
      'Owner',
      'Decommt',
      'Decompa',
      'Decounm',
      'Detotal',
      'Actotal',
      'Desubdt',
      'Odlname',
      'Pacomnm',
      'Delamnm',
      'Lacomnm',
      'Delamcn',
      'Decsunm',
      'Decomds',
      'Customer_kpi_section',
      'Kpi_dashboard_name',
      'Gblname',
      'Desk',
      'Deetadt',
      'Doityourself_ds',
      'Oddesk',
      'Vnam',
      'Vrep',
      'Debpref',
      'Dedvref',
      'Deveref',
      'Stocktype',
      'Denamnm'
    ]
  };
}